import { HeaderComponent } from '../../layout/components/HeaderComponent.tsx';
import { Localized, useTranslation } from 'dg-web-shared/lib/Localization.tsx';
import { ManageAccounts } from '@mui/icons-material';
import { SeparationBar } from '../../layout/components/SeparationBar.tsx';
import { MainContent } from '../../shared-mui-components/MainContent.tsx';
import { useOperatorContext } from '../../app/components/BaseLayoutAndData.tsx';
import { EmDash } from 'dg-web-shared/lib/Punctuation.ts';
import { ButtonWithDropdown } from '../../layout/components/ButtonWithDropdown.tsx';
import { useNavigate } from 'react-router-dom';
import {
    FormContentLayout,
    FormLeftColumn,
    FormRightColumn,
} from '../../layout/components/form/FormLayout.tsx';
import { ParkingaboLoginDivideBar } from './OperatorLoginDetailForm.tsx';
import { ReadOnlyTextField } from '../../ui/material-ui/ReadOnlyTextField.tsx';
import { Box, Stack, Switch } from '@mui/material';
import {
    ConfirmationDialog,
    ConfirmationDialogVersions,
} from './ConfirmationDialog.tsx';
import {
    RequestMethod,
    RequestStatus,
    useServerSuccessEffect,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { useState } from 'react';
import { useUpdate } from 'dg-web-shared/lib/Flux.tsx';
import * as OperatorLoginsState from '../state/OperatorLoginsState.ts';
import * as CommonOperatorLoginsState from '../../common/state/OperatorLoginsState.ts';
import * as CurrentOperatorLoginState from '../../common/state/CurrentOperatorLoginState.ts';
import { OtpOutlet } from './ConfirmOtpModal.tsx';
import { HeaderCancelSaveButtons } from '../../layout/components/HeaderCancelSaveButtons.tsx';
import { Control, useForm } from 'react-hook-form';
import { ValidationData } from 'dg-web-shared/lib/forms/FormValidationHelpers.tsx';
import { ReactHookFormTextField } from 'dg-web-shared/common/components/material-ui/react-hook-form-fields/ReactHookFormTextField.tsx';
import { tooManyCharsMessage } from 'dg-web-shared/common/components/translations/FormValidationErrorTexts.ts';
import { ReactHookFormSwitch } from 'dg-web-shared/common/components/material-ui/react-hook-form-fields/ReactHookFormSwitch.tsx';
import { OperatorLoginRole } from '../../common/state/OperatorLoginData.ts';

const enum OperatorLoginProfileFormFields {
    description = 'description',
    telnr = 'telnr',
    parkingMeterAlerts = 'parkingMeterAlerts',
    cloudConnectorAlerts = 'cloudConnectorAlerts',
}

interface OperatorLoginProfileFormValues {
    [OperatorLoginProfileFormFields.description]: string;
    [OperatorLoginProfileFormFields.telnr]: string;
    [OperatorLoginProfileFormFields.parkingMeterAlerts]: boolean;
    [OperatorLoginProfileFormFields.cloudConnectorAlerts]: boolean;
}

interface OperatorLoginProfilePayload extends OperatorLoginProfileFormValues {
    operatorLoginId: number | null;
}

export function OperatorLoginProfilePage() {
    const { currentLogin, operatorExternalId } = useOperatorContext();
    const navigate = useNavigate();

    const [disableOtpWarningOpen, setDisableOtpWarningOpen] = useState(false);
    const [updateOtpEnabledState, updateOtpEnabled] =
        useUpdateOtpStatusServerWrite(currentLogin.operatorLoginId);
    const update = useUpdate();

    useServerSuccessEffect(updateOtpEnabledState, data => {
        if (data == null) {
            update(store => {
                OperatorLoginsState.Edit.reset(store);
                CommonOperatorLoginsState.List.reset(store);
                CurrentOperatorLoginState.reset(store);
                return 'refetch-logins-after-otp-disable';
            });
        } else {
            navigate(
                `/${operatorExternalId}/profile/${currentLogin.operatorLoginId}/confirm-otp/${encodeURIComponent(data.otpUrl)}`,
            );
        }
    });

    const {
        control,
        handleSubmit,
        formState: { isDirty },
        reset,
    } = useForm<OperatorLoginProfileFormValues>({
        defaultValues: {
            [OperatorLoginProfileFormFields.description]:
                currentLogin.description ?? '',
            [OperatorLoginProfileFormFields.telnr]: currentLogin.telnr ?? '',
            [OperatorLoginProfileFormFields.parkingMeterAlerts]:
                currentLogin.permissions.parkingMeterAlerts,
            [OperatorLoginProfileFormFields.cloudConnectorAlerts]:
                currentLogin.permissions.cloudConnectorAlerts,
        },
        mode: 'onBlur',
    });

    const [updateProfileState, updateProfile] = useServerWrite<
        OperatorLoginProfilePayload,
        null,
        ValidationData
    >(() => ({
        url: `/ui-api/operator-account/operator-logins/${currentLogin.operatorLoginId}/profile`,
        method: RequestMethod.PUT,
    }));
    useServerSuccessEffect(updateProfileState, () => {
        update(CurrentOperatorLoginState.reset);
    });
    const maxDescriptionLength = 25;
    const maxDescriptionMessage = useTranslation(
        tooManyCharsMessage(maxDescriptionLength),
    );
    const phoneInvalidCharsMessage = useTranslation({
        de: 'Es sind nur Zahlen, + und Leerzeichen zulässig.',
        fr: 'Seuls les chiffres, + et les espaces sont autorisés.',
        it: 'Sono consentiti solo numeri, + e spazi.',
        en: 'Only numbers, +, and spaces are allowed.',
    });
    const isAdmin = currentLogin.role === OperatorLoginRole.ADMIN;

    return (
        <MainContent>
            <HeaderComponent
                title={
                    <>
                        <Localized
                            de="Profil"
                            fr="Profil"
                            it="Profilo"
                            en="Profile"
                        />
                        {` ${EmDash} `}
                        {`${currentLogin.firstname} ${currentLogin.lastname}`}
                    </>
                }
                Icon={ManageAccounts}
                editMode={isDirty}
                buttons={
                    isDirty ? (
                        <HeaderCancelSaveButtons
                            onCancel={reset}
                            onSave={handleSubmit(
                                (fields: OperatorLoginProfileFormValues) =>
                                    updateProfile({
                                        operatorLoginId:
                                            currentLogin.operatorLoginId,
                                        description: fields.description,
                                        telnr: fields.telnr,
                                        parkingMeterAlerts:
                                            fields.parkingMeterAlerts,
                                        cloudConnectorAlerts:
                                            fields.cloudConnectorAlerts,
                                    }),
                            )}
                            loading={
                                updateProfileState.status ===
                                RequestStatus.PENDING
                            }
                        />
                    ) : (
                        <ButtonWithDropdown
                            label={
                                <Localized
                                    de="Aktionen"
                                    fr="Actions"
                                    it="Azioni"
                                    en="Actions"
                                />
                            }
                            itemVariant={'action'}
                            dropdownItems={[
                                {
                                    key: 'reset-password',
                                    label: (
                                        <Localized
                                            de="Passwort ändern"
                                            fr="Modifier le mot de passe"
                                            it="Modifica password"
                                            en="Change password"
                                        />
                                    ),
                                    onClickCallback: () => navigate('password'),
                                    disabled: false,
                                },
                            ]}
                        />
                    )
                }
            />
            <SeparationBar />
            <Box
                sx={{
                    display: 'flex',
                    padding: '20px 24px',
                    flexGrow: 1,
                }}
            >
                <FormContentLayout>
                    <FormLeftColumn>
                        <ParkingaboLoginDivideBar
                            title={
                                <Localized
                                    de="Allgemein"
                                    fr="Général"
                                    it="Generale"
                                    en="General"
                                />
                            }
                        />
                        <Stack direction={'row'} spacing={2}>
                            <Box sx={{ flexGrow: 1 }}>
                                <ReadOnlyTextField
                                    label={
                                        <Localized
                                            de="Name"
                                            fr="Nom"
                                            it="Nome"
                                            en="Name"
                                        />
                                    }
                                    value={currentLogin.lastname}
                                />
                            </Box>
                            <Box sx={{ flexGrow: 1 }}>
                                <ReadOnlyTextField
                                    label={
                                        <Localized
                                            de="Vorname"
                                            fr="Prénom"
                                            it="Nome"
                                            en="Firstname"
                                        />
                                    }
                                    value={currentLogin.firstname}
                                />
                            </Box>
                        </Stack>
                        {isAdmin ? (
                            <ReactHookFormTextField
                                label={
                                    <Localized
                                        de="Beschreibung"
                                        fr="Description"
                                        it="Descrizione"
                                        en="Description"
                                    />
                                }
                                name={
                                    OperatorLoginProfileFormFields.description
                                }
                                control={control}
                                trimOnBlur={true}
                            />
                        ) : (
                            <ReadOnlyTextField
                                label={
                                    <Localized
                                        de="Beschreibung"
                                        fr="Description"
                                        it="Descrizione"
                                        en="Description"
                                    />
                                }
                                value={currentLogin.description || EmDash}
                            />
                        )}
                        {isAdmin ? (
                            <ReactHookFormTextField
                                label={
                                    <Localized
                                        de="Telefon"
                                        fr="Téléphone"
                                        it="Telefono"
                                        en="Phone"
                                    />
                                }
                                name={OperatorLoginProfileFormFields.telnr}
                                control={control}
                                rules={{
                                    maxLength: {
                                        value: maxDescriptionLength,
                                        message: maxDescriptionMessage,
                                    },
                                    validate: {
                                        isPhoneValid: (value: string) => {
                                            if (value === '') {
                                                return true;
                                            }

                                            if (
                                                !/^[+0-9][0-9 ]*$/.test(value)
                                            ) {
                                                return phoneInvalidCharsMessage;
                                            }

                                            return true;
                                        },
                                    },
                                }}
                                trimOnBlur={true}
                            />
                        ) : (
                            <ReadOnlyTextField
                                label={
                                    <Localized
                                        de="Telefon"
                                        fr="Téléphone"
                                        it="Telefono"
                                        en="Phone"
                                    />
                                }
                                value={currentLogin.telnr || EmDash}
                            />
                        )}
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="E-Mail"
                                    fr="E-mail"
                                    it="E-mail"
                                    en="E-mail"
                                />
                            }
                            value={
                                currentLogin.email ? currentLogin.email : EmDash
                            }
                        />
                    </FormLeftColumn>
                    <FormRightColumn>
                        <ParkingaboLoginDivideBar
                            title={
                                <Localized
                                    de="Zugriff"
                                    fr="Accès"
                                    it="Accesso"
                                    en="Access"
                                />
                            }
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Benutzername"
                                    fr="Nom utilisateur"
                                    it="Nome utente"
                                    en="Username"
                                />
                            }
                            value={currentLogin.username}
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Passwort"
                                    fr="Mot de passe"
                                    it="Password"
                                    en="Password"
                                />
                            }
                            value={
                                <Box
                                    sx={{
                                        fontStyle: 'italic',
                                    }}
                                >
                                    <Localized
                                        de="kann unter Aktionen geändert werden"
                                        fr="peut être modifié sous Actions"
                                        it="può essere modificato in Azioni"
                                        en="can be changed under Actions"
                                    />
                                </Box>
                            }
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Zwei-Faktor-Authentifizierung"
                                    fr="Authentification à deux facteurs"
                                    it="Autenticazione a due fattori"
                                    en="Two-factor authentication"
                                />
                            }
                            value={
                                <Switch
                                    checked={currentLogin.otpEnabled}
                                    onClick={() =>
                                        currentLogin.otpEnabled
                                            ? setDisableOtpWarningOpen(true)
                                            : updateOtpEnabled({
                                                  enable: true,
                                              })
                                    }
                                    disabled={isDirty}
                                />
                            }
                        />
                        <OperatorLoginProfileEmailNotifications
                            control={control}
                        />
                    </FormRightColumn>
                </FormContentLayout>
                <OtpDisableWaringModal
                    open={disableOtpWarningOpen}
                    onCancel={() => setDisableOtpWarningOpen(false)}
                    onConfirm={() =>
                        updateOtpEnabled({
                            enable: false,
                        })
                    }
                />
                <OtpOutlet context={{}} />
            </Box>
        </MainContent>
    );
}

export function OtpDisableWaringModal({
    open,
    onCancel,
    onConfirm,
}: {
    open: boolean;
    onCancel: () => void;
    onConfirm: () => void;
}) {
    return (
        <ConfirmationDialog
            open={open}
            title={
                <Localized
                    de="2FA ausschalten"
                    fr="Désactiver 2FA"
                    it="Disattivare 2FA"
                    en="Disable 2FA"
                />
            }
            onConfirm={onConfirm}
            onCancel={onCancel}
            confirmLabel={
                <Localized
                    de="Ausschalten"
                    fr="Désactiver"
                    it="Disattiva"
                    en="Disable"
                />
            }
            cancelLabel={
                <Localized
                    de="Abbrechen"
                    fr="Annuler"
                    it="Annulla"
                    en="Cancel"
                />
            }
            dialogVersion={ConfirmationDialogVersions.SOFT_WARNING}
        >
            <p>
                <Localized
                    de="Möchten Sie die Zwei-Faktor-Authentifizierung wirklich ausschalten?"
                    fr="Voulez-vous vraiment désactiver l’authentification à deux facteurs?"
                    it="Vuole davvero disattivare l’autenticazione a due fattori?"
                    en="Do you really want to disable two-factor authentication?"
                />
            </p>
        </ConfirmationDialog>
    );
}

export interface OtpStatusRequest {
    enable: boolean;
}

export interface OtpResponse {
    otpUrl: string;
}

export function useUpdateOtpStatusServerWrite(operatorLoginId: number | null) {
    return useServerWrite<OtpStatusRequest, OtpResponse | null>(() => ({
        url: `/ui-api/operator-account/operator-logins/${operatorLoginId}/otp-status`,
        method: RequestMethod.PUT,
    }));
}

function OperatorLoginProfileEmailNotifications({
    control,
}: {
    control: Control<OperatorLoginProfileFormValues>;
}) {
    const { operatorData } = useOperatorContext();

    if (!operatorData.hasTaxomexModule && !operatorData.hasCloudConnector) {
        return null;
    }

    return (
        <Stack spacing={1}>
            <ParkingaboLoginDivideBar
                title={
                    <Localized
                        de="E-Mail-Benachrichtigungen"
                        fr="Notifications e-mail"
                        it="Notifiche e-mail"
                        en="E-mail alerts"
                    />
                }
            />
            {operatorData.hasTaxomexModule && (
                <ReactHookFormSwitch
                    label={
                        <Localized
                            de="TOMeco"
                            fr="TOMeco"
                            it="TOMeco"
                            en="TOMeco"
                        />
                    }
                    name={OperatorLoginProfileFormFields.parkingMeterAlerts}
                    control={control}
                />
            )}
            {operatorData.hasCloudConnector && (
                <ReactHookFormSwitch
                    label={
                        <Localized
                            de="Cloud Connector"
                            fr="Cloud Connector"
                            it="Cloud Connector"
                            en="Cloud Connector"
                        />
                    }
                    name={OperatorLoginProfileFormFields.cloudConnectorAlerts}
                    control={control}
                />
            )}
        </Stack>
    );
}
